import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import { useExtensionWorkers, ExtensionWorkerListItem } from '../hooks/useExtensionWorkers';
import { useProviderDisplayNames } from '../hooks/useProviderDisplayNames';
import { useGeneralConfig } from '../hooks/useGeneralConfig';
import { useForgetExtensionWorkerCapability } from '../hooks/useForgetExtensionWorkerCapability';
import { useUpdateExtensionWorkerMaxTabs } from '../hooks/useUpdateExtensionWorkerMaxTabs';
import { deleteExtensionWorker, useDeleteExtensionWorker } from '../hooks/useDeleteExtensionWorker';

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'idle':
      return 'success';
    case 'busy':
      return 'warning';
    case 'offline':
      return 'error';
    default:
      return 'default';
  }
};

const formatRelativeTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const workerMayBeDeleted = (status: string): boolean =>
  status === 'offline' || status === 'disabled';

const TAB_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const ExtensionWorkersPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useExtensionWorkers();
  const { config: generalConfig } = useGeneralConfig();
  const { mutate: updateMaxTabs, isLoading: isUpdatingTabs } = useUpdateExtensionWorkerMaxTabs();
  const { mutate: forgetCapability, isLoading: isForgetting } =
    useForgetExtensionWorkerCapability();
  const { mutate: deleteWorker, isLoading: isDeleting } = useDeleteExtensionWorker();
  const [updatingWorkerId, setUpdatingWorkerId] = useState<string | null>(null);
  const [workerToDelete, setWorkerToDelete] = useState<ExtensionWorkerListItem | null>(null);
  const [offlineDeleteOpen, setOfflineDeleteOpen] = useState(false);
  const [isPruningOffline, setIsPruningOffline] = useState(false);
  const preferredWorkerIds = new Set(generalConfig?.brand_scan_preferred_worker_ids || []);
  const providerIds = useMemo(
    () => (data?.workers || []).map(w => w.provider_id).filter((id): id is string => !!id),
    [data?.workers]
  );
  const { displayNames } = useProviderDisplayNames(providerIds);

  const resolveName = (worker: {
    display_name?: string | null;
    provider_id?: string | null;
    worker_id: string;
  }) => {
    if (worker.display_name) return worker.display_name;
    if (worker.provider_id && displayNames[worker.provider_id]) {
      return displayNames[worker.provider_id];
    }
    return worker.worker_id.slice(0, 8);
  };

  const handleMaxTabsChange = (workerId: string, value: number) => {
    setUpdatingWorkerId(workerId);
    updateMaxTabs(
      { workerId, maxConcurrentTabs: value },
      { onSettled: () => setUpdatingWorkerId(null) }
    );
  };

  const handleConfirmDeleteWorker = () => {
    if (!workerToDelete) return;
    deleteWorker(workerToDelete.worker_id, {
      onSettled: () => setWorkerToDelete(null),
    });
  };

  const handleConfirmPruneOffline = async () => {
    const ids = (data?.workers || []).filter(w => w.status === 'offline').map(w => w.worker_id);
    if (!ids.length) {
      setOfflineDeleteOpen(false);
      return;
    }
    setIsPruningOffline(true);
    try {
      await Promise.all(ids.map(id => deleteExtensionWorker(id)));
      await queryClient.invalidateQueries('extensionWorkers');
      await queryClient.invalidateQueries('generalConfig');
      toast.success(`Removed ${ids.length} offline worker${ids.length === 1 ? '' : 's'}`);
      setOfflineDeleteOpen(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Failed to remove offline workers');
    } finally {
      setIsPruningOffline(false);
    }
  };

  const summary = data?.summary;
  const offlineCount = summary?.offline ?? 0;

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Extension Workers
          {isLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Analyst Chrome instances with Collector listener mode enabled. Polls every ~15s; workers
          go offline after {data?.offline_threshold_seconds ?? 120}s without a heartbeat. Reloading
          or reinstalling the extension creates a new worker ID; leftover offline rows can be
          deleted here. Max tabs is stored on the worker (one value, two editors). Change it here or
          in the Collector extension options; the extension syncs on the next heartbeat or claim.
          Each work tab runs in parallel. Marketplace chips are hosts this browser has successfully
          scraped or that the analyst marked ready after login/CAPTCHA. Remove a chip to forget that
          preference.
        </Typography>

        {isError && (
          <Typography color="error" variant="body2">
            Failed to load extension workers.
          </Typography>
        )}

        {summary && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip
              label={`Online: ${summary.online}`}
              size="small"
              color="success"
              variant="outlined"
            />
            <Chip label={`Idle: ${summary.idle}`} size="small" color="success" />
            <Chip label={`Busy: ${summary.busy}`} size="small" color="warning" />
            <Chip
              label={`Offline: ${summary.offline}`}
              size="small"
              color="error"
              variant="outlined"
            />
            <Chip label={`Disabled: ${summary.disabled}`} size="small" />
            <Chip
              label={`Queued work: ${summary.queued_work_items}`}
              size="small"
              color={summary.queued_work_items > 0 ? 'warning' : 'default'}
              variant="outlined"
            />
            {offlineCount > 0 && (
              <Button
                size="small"
                color="error"
                onClick={() => setOfflineDeleteOpen(true)}
                disabled={isPruningOffline || isDeleting}
              >
                Remove offline
              </Button>
            )}
          </Box>
        )}

        {!isLoading && data && data.workers.length === 0 && (
          <Typography color="textSecondary" variant="body2">
            No extension workers registered yet. Enable listener mode in the Collector extension
            options to join the pool.
          </Typography>
        )}

        {data && data.workers.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Analyst</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Max tabs</TableCell>
                  <TableCell>Current task</TableCell>
                  <TableCell>
                    <Tooltip title="Sites this browser has scraped or pinned. A scale worker can take client work for any marketplace the latest suite passed, even when this list is empty. A pinned site stays on that browser.">
                      <span>Marketplaces</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Last seen</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.workers.map(worker => {
                  const maxTabs = worker.max_concurrent_tabs ?? 1;
                  const activeClaims = worker.active_claim_count ?? 0;
                  const statusLabel =
                    activeClaims > 0
                      ? `${worker.status} (${activeClaims}/${maxTabs})`
                      : worker.status;
                  return (
                    <TableRow key={worker.worker_id} hover>
                      <TableCell>
                        <Typography variant="body2">{resolveName(worker)}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          <Typography variant="caption" color="textSecondary">
                            {worker.worker_id.slice(0, 8)}…
                          </Typography>
                          {preferredWorkerIds.has(worker.worker_id) && (
                            <Chip label="Brand scan preferred" size="small" color="primary" />
                          )}
                          {worker.worker_role === 'priming' && (
                            <Chip label="Priming" size="small" color="secondary" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={statusLabel} size="small" color={statusColor(worker.status)} />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={maxTabs}
                          disabled={
                            (isUpdatingTabs && updatingWorkerId === worker.worker_id) ||
                            worker.status === 'offline'
                          }
                          onChange={e =>
                            handleMaxTabsChange(worker.worker_id, Number(e.target.value))
                          }
                          sx={{ minWidth: 72 }}
                        >
                          {TAB_OPTIONS.map(n => (
                            <MenuItem key={n} value={n}>
                              {n}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        {worker.current_task_name ? (
                          <>
                            <Typography variant="body2">{worker.current_task_name}</Typography>
                            {worker.current_url && (
                              <Link
                                href={worker.current_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="caption"
                                sx={{ wordBreak: 'break-all' }}
                              >
                                {worker.current_url}
                              </Link>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(worker.capabilities || []).length === 0 ? (
                          <Typography variant="body2" color="textSecondary">
                            —
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(worker.capabilities || []).map(cap => (
                              <Chip
                                key={cap.marketplace_key}
                                label={cap.marketplace_key}
                                size="small"
                                variant="outlined"
                                title={
                                  cap.source === 'manual'
                                    ? 'Pinned to this browser. Other workers will not take this site.'
                                    : 'This browser has scraped this site. Other workers can still take it.'
                                }
                                onDelete={() =>
                                  forgetCapability({
                                    workerId: worker.worker_id,
                                    marketplaceKey: cap.marketplace_key,
                                  })
                                }
                                disabled={isForgetting}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{worker.tasks_completed}</TableCell>
                      <TableCell>{worker.extension_version || '—'}</TableCell>
                      <TableCell>{formatRelativeTime(worker.last_seen_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            workerMayBeDeleted(worker.status)
                              ? 'Remove this worker record'
                              : 'Online workers cannot be removed'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={
                                !workerMayBeDeleted(worker.status) || isDeleting || isPruningOffline
                              }
                              onClick={() => setWorkerToDelete(worker)}
                              aria-label="Remove extension worker"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <Dialog open={Boolean(workerToDelete)} onClose={() => setWorkerToDelete(null)}>
        <DialogTitle>Remove extension worker</DialogTitle>
        <DialogContent>
          <Typography>
            Remove this worker record? Claimed work is returned to the queue. If this Chrome
            instance is still installed, it will reappear on the next heartbeat.
          </Typography>
          {workerToDelete && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {resolveName(workerToDelete)} · {workerToDelete.worker_id.slice(0, 8)}… ·{' '}
              {workerToDelete.extension_version || 'unknown version'} · {workerToDelete.status}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkerToDelete(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteWorker}
            disabled={isDeleting}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={offlineDeleteOpen} onClose={() => setOfflineDeleteOpen(false)}>
        <DialogTitle>Remove offline workers</DialogTitle>
        <DialogContent>
          <Typography>
            Remove {offlineCount} offline worker{offlineCount === 1 ? '' : 's'}? These are typically
            leftover IDs from an extension reload or reinstall. Disabled workers (listener off) are
            left in place.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfflineDeleteOpen(false)} disabled={isPruningOffline}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmPruneOffline}
            disabled={isPruningOffline}
          >
            {isPruningOffline ? 'Removing…' : 'Remove offline'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ExtensionWorkersPanel;
